import { useContext, useEffect, useMemo, useState } from "react"
import { DateTime } from "luxon"

//Activity components:
import ActivitySetting from "./ActivitySetting"

//Context
import { UserActivityContext } from "../../../context/userActivityContext"

//Helpers
import { getEnvironmentCode } from "../developerToolsHelpers"
import {
  activityTrackingSettingNames,
  getSettingOverrideValue,
  setSettingOverrideValue,
  settingsList,
} from "./activitySettingsHelpers"

//PrimeReact
import { Button } from "primereact/button"

//Styles
import devToolStyles from "../../../styles/developerTools/DevTools.module.css"
import myStyles from "../../../styles/developerTools/DevToolsServiceUrls.module.css"
import useActivityConfig from "../../../hooks/activityTracker/useActivityConfig"

export default function DevToolActivityTracker({
  expandedCode,
  onSetExpandedCode,
  onUpdated,
}) {
  const cardCode = "ActivityTracker"

  const userActivityCtx = useContext(UserActivityContext)
  const { getLogoutAfter, getWarningDuration, loadingConfig } = useActivityConfig()
  
  const [settings, setSettings] = useState([])
  const [reloadSettings, setReloadSettings] = useState(true)
  const [useLastActive, setUseLastActive] = useState(userActivityCtx?.getLastActive?.() || Date.now())

  const doDisableAutoLogout =
    process.env.REACT_APP_DISABLE_AUTO_LOGOUT === "TRUE"

  const hasChanges = settings.some((setting) => setting.newOverride)
const isExpanded = useMemo(() => cardCode === expandedCode, [cardCode, expandedCode]);
  const hasOverrides = settings.some(
    (setting) => setting.activeOverride?.overrideValue,
  )

  const lastActivityTimestamp = DateTime.fromMillis(useLastActive)
  const expectLogoutWarning = lastActivityTimestamp?.plus({
    milliseconds: userActivityCtx.getLogoutAfter() || 0,
  })

  const logoutAfter = userActivityCtx.getLogoutAfter()
  const warningDuration = userActivityCtx.getWarningDuration()

  useEffect(() => {
    const getLastActive = userActivityCtx?.getLastActive;
    
    if (isExpanded && getLastActive) {
      const update = () => setUseLastActive(getLastActive());
      update();
      
      const interval = setInterval(update, 1000);
      return () => clearInterval(interval);
    }
  }, [userActivityCtx?.getLastActive, isExpanded]);

  useEffect(() => {
    const environmentCode = getEnvironmentCode()

    let localSettingList = settingsList.map((setting) => {
      const envVar = `${setting.name}${environmentCode}`
      let defaultSetting = setting.name === activityTrackingSettingNames.logoutTimeout ? getLogoutAfter() / 60000 : getWarningDuration() / 1000

      let newSetting = {
        ...setting,
        defaultSetting,
        newOverride: "",
        storageKey: envVar,
        activeOverride: getSettingOverrideValue(envVar),
      }

      newSetting.status = newSetting.activeOverride?.overrideValue
        ? "OVERRIDE"
        : ""

      return newSetting
    })

    setSettings(localSettingList)
  }, [reloadSettings, loadingConfig])

  const saveOverrides = (newSettings) => {
    const useSettings = newSettings || settings

    useSettings.forEach((setting) => {
      if (
        setting.newOverride === setting.defaultSetting &&
        setting.activeOverride
      ) {
        setSettingOverrideValue(setting.storageKey, "")
      } else if (
        setting.newOverride &&
        setting.newOverride !== setting.defaultSetting
      ) {
        setSettingOverrideValue(setting.storageKey, setting.newOverride)
      }
    })

    userActivityCtx.refreshSettings()
    setReloadSettings(!reloadSettings)
    onUpdated()
  }

  const resetAllSettings = () => {
    const newSettings = settings.map((setting) => {
      return {
        ...setting,
        newOverride: setting.defaultSetting,
      }
    })

    setSettings(newSettings)
    userActivityCtx.refreshSettings()
    setReloadSettings(!reloadSettings)
    saveOverrides(newSettings)
  }

  return (
    <div
      className={`${devToolStyles.cardLayout} ${
        expandedCode === cardCode ? devToolStyles.cardLayoutExpanded : ""
      } ${
        expandedCode !== "" && expandedCode !== cardCode
          ? devToolStyles.cardLayoutHidden
          : ""
      }`}
      onClick={() => {
        onSetExpandedCode(cardCode)
      }}
    >
      <div className={devToolStyles.cardLayoutTitle}>Activity tracker</div>
      <div className={devToolStyles.cardLayoutStatus}>
        {hasOverrides && !doDisableAutoLogout ? (
          <div
            className={`${devToolStyles.activeBadge} ${
              isExpanded ? devToolStyles.expandedBadge : ""
            }`}
          >
            {isExpanded ? "ACTIVE" : ""}
          </div>
        ) : null}
        {doDisableAutoLogout ? (
          <div
            className={`${devToolStyles.disabledBadge} ${
              isExpanded ? devToolStyles.expandedDisableBadge : ""
            }`}
          >
            {isExpanded ? "DISABLED" : ""}
          </div>
        ) : null}
      </div>
      <div className={devToolStyles.cardLayoutDescription}>
        Activity tracker settings
      </div>
      <div
        className={`${devToolStyles.cardLayoutBody} ${myStyles.serviceList}`}
      >
        <div className={devToolStyles.subTitle}>Active settings</div>
        <div>
          Inactive warning timeout:{" "}
          {logoutAfter
            ? `${logoutAfter / 60000} minutes`
            : "Unavailable"}
        </div>
        <div>
          Warning duration:{" "}
          {warningDuration
            ? `${warningDuration / 1000} seconds`
            : "Unavailable"}
        </div>

        <div className={devToolStyles.divider}></div>
        <div className={devToolStyles.subTitle}>Current status</div>
        {!doDisableAutoLogout ? (
          <>
            <div>
              Last activity:{" "}
              {lastActivityTimestamp.isValid
                ? lastActivityTimestamp.toLocaleString(
                    DateTime.DATETIME_MED_WITH_SECONDS,
                  )
                : "No activity recorded"}
            </div>
            <div>
              Expect logout warning:{" "}
              {expectLogoutWarning?.isValid
                ? expectLogoutWarning.toLocaleString(
                    DateTime.DATETIME_MED_WITH_SECONDS,
                  )
                : "No activity recorded"}
            </div>
          </>
        ) : (
          <>Disabled: Enable in .env to activate</>
        )}

        <div className={devToolStyles.divider}></div>
        {settings.map((setting) => (
          <ActivitySetting
            setting={setting}
            key={setting.name}
            onOverride={(overrideValue, isReset) => {
              let newSetting = { ...setting, newOverride: overrideValue }

              if (newSetting.newOverride === newSetting.defaultSetting) {
                newSetting.status = ""
              }

              const index = settings.indexOf(setting)

              let newSettings = [...settings]
              newSettings[index] = newSetting

              setSettings(newSettings)

              if (isReset) {
                saveOverrides([newSetting])
              }
            }}
          />
        ))}
      </div>
      <div className={devToolStyles.cardLayoutFooter}>
        <Button
          label="Reset all"
          type="button"
          className={`${devToolStyles.button} ${devToolStyles.noBackground}`}
          onClick={resetAllSettings}
          disabled={!hasChanges && !hasOverrides}
        />
        <Button
          label="Save"
          type="button"
          className={`${devToolStyles.button}`}
          onClick={() => {
            saveOverrides()
          }}
          disabled={!hasChanges}
        />
      </div>
    </div>
  )
}
